// File: clientapp/src/pages/SecurityRoleDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    TextField,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export default function SecurityRoleDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [roleDetail, setRoleDetail] = useState({
        id: "",
        name: "",
        groups: [],    // groups assigned to the role: { groupId, name, description }
        features: []   // features assigned to the role: { featureId, name, description }
    });
    const [saveMessage, setSaveMessage] = useState("");

    // States for managing groups for the role
    const [groupSearch, setGroupSearch] = useState("");
    const [groupPage, setGroupPage] = useState(0);
    const [groupRowsPerPage, setGroupRowsPerPage] = useState(10);

    // States for Add Group Dialog for role
    const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [availGroupPage, setAvailGroupPage] = useState(0);
    const [availGroupRowsPerPage, setAvailGroupRowsPerPage] = useState(10);

    // States for managing features for the role
    const [featureSearch, setFeatureSearch] = useState("");
    const [featurePage, setFeaturePage] = useState(0);
    const [featureRowsPerPage, setFeatureRowsPerPage] = useState(10);

    // States for Add Feature Dialog for role
    const [addFeatureDialogOpen, setAddFeatureDialogOpen] = useState(false);
    const [availableFeatures, setAvailableFeatures] = useState([]);
    const [availFeaturePage, setAvailFeaturePage] = useState(0);
    const [availFeatureRowsPerPage, setAvailFeatureRowsPerPage] = useState(10);

    useEffect(() => {
        if (!isNew) {
            fetchRoleDetail();
        }
    }, [id, isNew]);

    async function fetchRoleDetail() {
        try {
            const response = await fetch(`/api/security/roles/${id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setRoleDetail(data);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    const handleSaveRole = async () => {
        try {
            const url = isNew ? `/api/security/roles` : `/api/security/roles/${id}`;
            const method = isNew ? 'POST' : 'PUT';
            const payload = { name: roleDetail.name };
            const response = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setSaveMessage("Role saved successfully.");
                if (isNew) {
                    const created = await response.json();
                    navigate(`/security/roles/${created.id}`);
                } else {
                    fetchRoleDetail();
                }
            } else {
                setSaveMessage("Failed to save role.");
            }
        } catch (err) {
            console.error(err);
            setSaveMessage("Error saving role.");
        }
    };

    // --- Group Management for Role ---

    const handleOpenAddGroupDialog = () => {
        setAddGroupDialogOpen(true);
        setAvailGroupPage(0);
        setGroupSearch("");
        fetchAvailableGroups("");
    };

    const handleCloseAddGroupDialog = () => {
        setAddGroupDialogOpen(false);
        setGroupSearch("");
        setAvailableGroups([]);
        setAvailGroupPage(0);
    };

    async function fetchAvailableGroups(searchTerm) {
        try {
            const response = await fetch(`/api/security/availableGroupsForRole?roleId=${id}&search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableGroups(data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleAddGroup = async (groupId) => {
        try {
            const response = await fetch(`/api/security/roles/${id}/groups`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groupId })
            });
            if (response.ok) {
                fetchRoleDetail();
            }
        } catch (err) {
            console.error(err);
        }
        handleCloseAddGroupDialog();
    };

    const handleRemoveGroup = async (groupId) => {
        if (window.confirm("Remove this group from the role?")) {
            try {
                const response = await fetch(`/api/security/roles/${id}/groups/${groupId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchRoleDetail();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleGroupPageChange = (event, newPage) => {
        setGroupPage(newPage);
    };

    const handleGroupRowsPerPageChange = (event) => {
        setGroupRowsPerPage(parseInt(event.target.value, 10));
        setGroupPage(0);
    };

    const paginatedGroups = roleDetail.groups
        ? roleDetail.groups.slice(groupPage * groupRowsPerPage, groupPage * groupRowsPerPage + groupRowsPerPage)
        : [];

    const handleAvailGroupPageChange = (event, newPage) => {
        setAvailGroupPage(newPage);
    };

    const handleAvailGroupRowsPerPageChange = (event) => {
        setAvailGroupRowsPerPage(parseInt(event.target.value, 10));
        setAvailGroupPage(0);
    };

    const paginatedAvailableGroups = availableGroups.slice(
        availGroupPage * availGroupRowsPerPage,
        availGroupPage * availGroupRowsPerPage + availGroupRowsPerPage
    );

    // --- Feature Management for Role ---

    const handleOpenAddFeatureDialog = () => {
        setAddFeatureDialogOpen(true);
        setAvailFeaturePage(0);
        setFeatureSearch("");
        fetchAvailableFeatures("");
    };

    const handleCloseAddFeatureDialog = () => {
        setAddFeatureDialogOpen(false);
        setFeatureSearch("");
        setAvailableFeatures([]);
        setAvailFeaturePage(0);
    };

    async function fetchAvailableFeatures(searchTerm) {
        try {
            const response = await fetch(`/api/security/availableFeaturesForRole?roleId=${id}&search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableFeatures(data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleAddFeature = async (featureId) => {
        try {
            const response = await fetch(`/api/security/roles/${id}/features`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ featureId })
            });
            if (response.ok) {
                fetchRoleDetail();
            }
        } catch (err) {
            console.error(err);
        }
        handleCloseAddFeatureDialog();
    };

    const handleRemoveFeature = async (featureId) => {
        if (window.confirm("Remove this feature from the role?")) {
            try {
                const response = await fetch(`/api/security/roles/${id}/features/${featureId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchRoleDetail();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleFeaturePageChange = (event, newPage) => {
        setFeaturePage(newPage);
    };

    const handleFeatureRowsPerPageChange = (event) => {
        setFeatureRowsPerPage(parseInt(event.target.value, 10));
        setFeaturePage(0);
    };

    const paginatedFeatures = roleDetail.features
        ? roleDetail.features.slice(featurePage * featureRowsPerPage, featurePage * featureRowsPerPage + featureRowsPerPage)
        : [];

    const handleAvailFeaturePageChange = (event, newPage) => {
        setAvailFeaturePage(newPage);
    };

    const handleAvailFeatureRowsPerPageChange = (event) => {
        setAvailFeatureRowsPerPage(parseInt(event.target.value, 10));
        setAvailFeaturePage(0);
    };

    const paginatedAvailableFeatures = availableFeatures.slice(
        availFeaturePage * availFeatureRowsPerPage,
        availFeaturePage * availFeatureRowsPerPage + availFeatureRowsPerPage
    );

    if (loading) {
        return (
            <Container>
                <Typography variant="body1">Loading...</Typography>
            </Container>
        );
    }

    return (
        <Container>
            {/* Header with Back and Save icons */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <Tooltip title="Back to Role List">
                        <IconButton color="primary" onClick={() => navigate('/security/roles')}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" ml={1}>Role Detail</Typography>
                </Box>
                <Tooltip title="Save Changes">
                    <IconButton color="primary" onClick={handleSaveRole}>
                        <SaveIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Basic Information */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Role Name"
                            value={roleDetail.name}
                            onChange={e => setRoleDetail({ ...roleDetail, name: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    {saveMessage && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="primary">{saveMessage}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Group Management Section */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle1" gutterBottom>Role Groups</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <TextField
                        label="Search Groups"
                        variant="outlined"
                        size="small"
                        value={groupSearch}
                        onChange={e => {
                            setGroupSearch(e.target.value);
                            setAvailGroupPage(0);
                            fetchAvailableGroups(e.target.value);
                        }}
                    />
                    <Tooltip title="Add Group">
                        <IconButton color="primary" onClick={handleOpenAddGroupDialog}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Group Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roleDetail.groups && roleDetail.groups.length > 0 ? (
                            roleDetail.groups.map(group => (
                                <TableRow key={group.groupId}>
                                    <TableCell>{group.name}</TableCell>
                                    <TableCell>{group.description}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Remove Group">
                                            <IconButton size="small" onClick={() => handleRemoveGroup(group.groupId)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3}>No groups assigned to this role.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={roleDetail.groups ? roleDetail.groups.length : 0}
                    page={groupPage}
                    onPageChange={handleGroupPageChange}
                    rowsPerPage={groupRowsPerPage}
                    onRowsPerPageChange={handleGroupRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Feature Management Section */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">Role Features</Typography>
                    <Tooltip title="Add Feature">
                        <IconButton color="primary" onClick={handleOpenAddFeatureDialog}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Feature Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roleDetail.features && roleDetail.features.length > 0 ? (
                            roleDetail.features.map((feature, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{feature.name}</TableCell>
                                    <TableCell>{feature.description}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Remove Feature">
                                            <IconButton size="small" onClick={() => handleRemoveFeature(feature.featureId)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3}>No features assigned to this role.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={roleDetail.features ? roleDetail.features.length : 0}
                    page={featurePage}
                    onPageChange={handleFeaturePageChange}
                    rowsPerPage={featureRowsPerPage}
                    onRowsPerPageChange={handleFeatureRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Add Group Dialog */}
            <Dialog open={addGroupDialogOpen} onClose={handleCloseAddGroupDialog} fullWidth maxWidth="md">
                <DialogTitle>Add Group to Role</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Search Available Groups"
                        variant="outlined"
                        fullWidth
                        value={groupSearch}
                        onChange={e => {
                            setGroupSearch(e.target.value);
                            setAvailGroupPage(0);
                            fetchAvailableGroups(e.target.value);
                        }}
                    />
                    <Paper style={{ maxHeight: 300, overflow: 'auto', marginTop: 16 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Group Name</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAvailableGroups.map(ag => (
                                    <TableRow key={ag.groupId}>
                                        <TableCell>{ag.name}</TableCell>
                                        <TableCell>{ag.description}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Add Group">
                                                <IconButton size="small" onClick={() => handleAddGroup(ag.groupId)}>
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <TablePagination
                        component="div"
                        count={availableGroups.length}
                        page={availGroupPage}
                        onPageChange={(e, newPage) => setAvailGroupPage(newPage)}
                        rowsPerPage={availGroupRowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setAvailGroupRowsPerPage(parseInt(e.target.value, 10));
                            setAvailGroupPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddGroupDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Add Feature Dialog */}
            <Dialog open={addFeatureDialogOpen} onClose={handleCloseAddFeatureDialog} fullWidth maxWidth="md">
                <DialogTitle>Add Feature to Role</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Search Available Features"
                        variant="outlined"
                        fullWidth
                        value={featureSearch}
                        onChange={e => {
                            setFeatureSearch(e.target.value);
                            setAvailFeaturePage(0);
                            fetchAvailableFeatures(e.target.value);
                        }}
                    />
                    <Paper style={{ maxHeight: 300, overflow: 'auto', marginTop: 16 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Feature Name</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAvailableFeatures.map(af => (
                                    <TableRow key={af.featureId}>
                                        <TableCell>{af.name}</TableCell>
                                        <TableCell>{af.description}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Add Feature">
                                                <IconButton size="small" onClick={() => handleAddFeature(af.featureId)}>
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <TablePagination
                        component="div"
                        count={availableFeatures.length}
                        page={availFeaturePage}
                        onPageChange={handleAvailFeaturePageChange}
                        rowsPerPage={availFeatureRowsPerPage}
                        onRowsPerPageChange={handleAvailFeatureRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddFeatureDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
